/**
 * TEAMS Area (teams-area-chat-shared-todos + teams-tier1-chat-batch
 *             + teams-hardening-and-shell)
 *
 * Internal collaboration spaces: each space has a chat feed and ONE shared
 * todo list every member can see and tick. Personal todos (HMSTodoList) are
 * a different, untouched surface.
 *
 * Tier 1 chat behaviour lives here: @mentions, quote-reply, edit, leave/mute,
 * scroll-back history, and the "new messages" divider.
 *
 * Liveness contract (Craig's anti-hammer rider on the 10s-polling decision):
 * ONE combined poll request every 10 seconds, and only while this screen is
 * mounted AND the tab is visible (document.hidden pauses the interval; the
 * backend rate-limits the poll endpoint on top). No polling anywhere else in
 * the app — the navbar entry deliberately has no live badge. The one
 * exception is the bounded catch-up below: when the server says a delta was
 * truncated it is answered immediately rather than 10s later, so a tab that
 * slept through a busy morning refills in seconds instead of minutes.
 *
 * Access: the backend 403 is the gate (internal staff only — customer traders
 * are excluded server-side); this screen renders a friendly wall on 403.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faBellSlash,
  faComments,
  faGear,
  faListCheck,
  faLock,
  faPaperPlane,
  faPen,
  faPlus,
  faReply,
  faRightFromBracket,
  faTimes,
  faTrash,
  faUserShield,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import teamsService from '../../services/teamsService';
import Layout from '../Layout';
import Footer from '../ui/Footer';
import ConfirmationPopup from '../ConfirmationPopup';
import TodoDatePicker from '../ui/TodoDatePicker';
import TeamsSelect from './TeamsSelect';
import logo from '../../assets/images/IMT_logo.png';
import './TeamsArea.css';

const POLL_INTERVAL_MS = 10000;
// How close to the top counts as "asking for older history".
const PAGE_TRIGGER_PX = 60;
// Bound on the catch-up chase after a truncated delta, so a runaway server
// answer can never turn into an unbounded request loop from the browser.
const MAX_CATCHUP_ROUNDS = 20;
// Matches an in-progress @token at the caret. Names carry spaces, so the
// query deliberately allows them and the member filter does the narrowing.
const MENTION_TOKEN = /@([^@\n]{0,30})$/;

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDayLabel(iso) {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/**
 * Which members this text actually points at.
 *
 * Read from the FINAL text against the real member list, not from what the
 * @-picker happened to insert. Typing "@Bianca Bunkers" by hand, or fixing a
 * typo in a name the picker inserted, used to produce a message that looked
 * like a mention and pinged nobody — the sender walked away believing someone
 * had been told. Longest names first so "@Ann Lee" wins over "@Ann".
 */
function resolveMentionsInText(text, members) {
  if (!text || !members || members.length === 0) return [];
  return [...members]
    .sort((a, b) => b.name.length - a.name.length)
    .filter((m) => text.includes(`@${m.name}`))
    .map((m) => m.id);
}

/** Renders a body with @Name runs highlighted. */
function MessageBody({ body, mentions }) {
  if (!mentions || mentions.length === 0) return <>{body}</>;
  // Longest names first so "@Ann Lee" wins over a hypothetical "@Ann".
  const names = [...mentions].sort((a, b) => b.name.length - a.name.length);
  const parts = [];
  let rest = body;
  let guard = 0;
  while (rest.length > 0 && guard < 200) {
    guard += 1;
    let hitIndex = -1;
    let hitName = null;
    names.forEach((m) => {
      const idx = rest.indexOf(`@${m.name}`);
      if (idx !== -1 && (hitIndex === -1 || idx < hitIndex)) {
        hitIndex = idx;
        hitName = m.name;
      }
    });
    if (hitIndex === -1) { parts.push(rest); break; }
    if (hitIndex > 0) parts.push(rest.slice(0, hitIndex));
    parts.push(
      <span className="teams-mention-chip" key={`${hitName}-${parts.length}`}>
        @{hitName}
      </span>,
    );
    rest = rest.slice(hitIndex + hitName.length + 1);
  }
  return <>{parts}</>;
}

function formatDueDate(iso) {
  if (!iso) return '';
  return new Date(`${iso}T00:00:00`).toLocaleDateString([], {
    day: '2-digit', month: 'short',
  });
}

/** Overdue is about the item still being open, not just the date passing. */
function isOverdue(todo) {
  if (!todo.due_date || todo.is_done) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(`${todo.due_date}T00:00:00`) < today;
}

/** Searchable multi-select over the internal colleague directory. */
function ColleaguePicker({ colleagues, excludeIds, selectedIds, onToggle }) {
  const [query, setQuery] = useState('');
  const excluded = new Set(excludeIds || []);
  const visible = colleagues.filter((c) => {
    if (excluded.has(c.id)) return false;
    const haystack = `${c.name} ${c.department}`.toLowerCase();
    return haystack.includes(query.toLowerCase());
  });
  return (
    <div className="teams-picker">
      <input
        type="text"
        className="teams-picker-search"
        placeholder="Search colleagues…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="teams-picker-list">
        {visible.map((c) => (
          <label key={c.id} className="teams-picker-row">
            <input
              type="checkbox"
              checked={selectedIds.includes(c.id)}
              onChange={() => onToggle(c.id)}
            />
            <span className="teams-picker-name">{c.name}</span>
            {c.department && (
              <span className="teams-picker-dept">{c.department}</span>
            )}
          </label>
        ))}
        {visible.length === 0 && (
          <div className="teams-picker-empty">No colleagues match.</div>
        )}
      </div>
    </div>
  );
}

/** Shared modal chrome so every Teams dialog behaves identically (backdrop
 *  click and Escape both close, the panel never swallows the page). */
function TeamsModal({ title, onClose, children, wide = false }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="teams-modal-backdrop" onClick={onClose}>
      <div
        className={`teams-modal ${wide ? 'wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="teams-modal-head">
          <h3>{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function TeamsArea() {
  const history = useHistory();
  const location = useLocation();

  const [spaces, setSpaces] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(null); // detail incl. members
  const [messages, setMessages] = useState([]);
  const [todos, setTodos] = useState([]);
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [errorBanner, setErrorBanner] = useState('');
  const [pollStalled, setPollStalled] = useState(false);
  // {message, confirmText, onConfirm} — the house confirm dialog, in place of
  // window.confirm's OS-chrome box.
  const [confirmPrompt, setConfirmPrompt] = useState(null);

  // Tier 1 state
  const [replyTo, setReplyTo] = useState(null);            // {id, author, excerpt}
  const [editing, setEditing] = useState(null);            // {id, body}
  const [firstUnreadId, setFirstUnreadId] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);  // string | null

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSpaceName, setNewSpaceName] = useState('');
  const [newSpaceDescription, setNewSpaceDescription] = useState('');
  const [newSpaceMemberIds, setNewSpaceMemberIds] = useState([]);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [addMemberIds, setAddMemberIds] = useState([]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsName, setSettingsName] = useState('');
  const [settingsDescription, setSettingsDescription] = useState('');
  const [handoverId, setHandoverId] = useState('');

  const [todoTitle, setTodoTitle] = useState('');
  const [todoAssignee, setTodoAssignee] = useState('');
  const [todoDueDate, setTodoDueDate] = useState('');
  const [editingTodo, setEditingTodo] = useState(null); // {id,title,assignee,due_date}

  const feedRef = useRef(null);
  const composerRef = useRef(null);
  const pollRef = useRef({ spaceId: null, since: null, busy: false });
  const stickToBottomRef = useRef(true);
  const prependRef = useRef(null); // scrollHeight captured before a prepend

  const surfaceError = useCallback((error, fallback) => {
    if (error?.response?.status === 403) {
      return; // membership/gate errors are handled by the callers
    }
    const detail =
      error?.response?.data?.detail ||
      (typeof error?.response?.data === 'object'
        ? Object.values(error.response.data).flat().join(' ')
        : '') ||
      fallback;
    setErrorBanner(String(detail));
  }, []);

  const applyMessages = useCallback((updater) => {
    setMessages((previous) => {
      const next = updater(previous);
      pollRef.current.since = next.length ? next[next.length - 1].id : 0;
      return next;
    });
  }, []);

  const appendMessages = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    applyMessages((previous) => {
      const known = new Set(previous.map((m) => m.id));
      const fresh = incoming.filter((m) => !known.has(m.id));
      return fresh.length ? [...previous, ...fresh] : previous;
    });
  }, [applyMessages]);

  /** Poll deltas can carry edits and tombstones for messages already on
   *  screen, so merge by id rather than blindly appending. */
  const mergeMessages = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    setMessages((previous) => {
      const byId = new Map(previous.map((m) => [m.id, m]));
      incoming.forEach((m) => byId.set(m.id, m));
      const merged = [...byId.values()].sort((a, b) => a.id - b.id);
      pollRef.current.since = merged.length ? merged[merged.length - 1].id : 0;
      return merged;
    });
  }, []);

  const closeSpace = useCallback(() => {
    setSelectedSpace(null);
    setMessages([]);
    setTodos([]);
    setReplyTo(null);
    setEditing(null);
    setEditingTodo(null);
    setFirstUnreadId(null);
    setHasMore(false);
    pollRef.current.spaceId = null;
    pollRef.current.since = null;
    history.replace({ pathname: location.pathname, search: '' });
  }, [history, location.pathname]);

  const openSpace = useCallback(async (spaceId) => {
    try {
      const [detail, feed, todoList] = await Promise.all([
        teamsService.getSpace(spaceId),
        teamsService.getMessages(spaceId),
        teamsService.listTodos(spaceId),
      ]);
      setSelectedSpace(detail);
      applyMessages(() => feed.messages);
      setFirstUnreadId(feed.first_unread_id);
      setHasMore(Boolean(feed.has_more));
      setTodos(todoList);
      setReplyTo(null);
      setEditing(null);
      setEditingTodo(null);
      pollRef.current.spaceId = spaceId;
      stickToBottomRef.current = true;
      // Opening marks the space read server-side; reflect it instantly.
      setSpaces((previous) =>
        previous.map((s) => (s.id === spaceId ? { ...s, unread: 0 } : s)),
      );
      history.replace({ pathname: location.pathname, search: `?space=${spaceId}` });
    } catch (error) {
      surfaceError(error, 'Could not open the space.');
      if (error?.response?.status === 403 || error?.response?.status === 404) {
        setErrorBanner('You are not a member of that space any more.');
        closeSpace();
      }
    }
  }, [applyMessages, closeSpace, history, location.pathname, surfaceError]);

  // Initial load: spaces + colleague directory (+ deep link ?space=N).
  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      try {
        const [spaceList, directory] = await Promise.all([
          teamsService.listSpaces(),
          teamsService.getColleagues(),
        ]);
        if (cancelled) return;
        setSpaces(spaceList);
        setColleagues(directory);
        const wanted = new URLSearchParams(location.search).get('space');
        if (wanted && spaceList.some((s) => s.id === Number(wanted))) {
          openSpace(Number(wanted));
        }
      } catch (error) {
        if (cancelled) return;
        if (error?.response?.status === 403) {
          setAccessDenied(true);
        } else {
          surfaceError(error, 'Could not load the Teams area.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    bootstrap();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // THE poll: one combined request per tick, paused while the tab is hidden.
  useEffect(() => {
    if (accessDenied) return undefined;

    /** One request. Returns true when the server truncated the delta and
     *  there is more waiting, so the caller can come straight back. */
    const fetchOnce = async () => {
      const { spaceId, since } = pollRef.current;
      const data = await teamsService.poll(spaceId, spaceId ? since : null);
      setSpaces(data.spaces);
      if (spaceId != null && pollRef.current.spaceId === spaceId) {
        if (data.messages) mergeMessages(data.messages);
        if (data.todos) setTodos(data.todos);
        setSpaces((previous) =>
          previous.map((s) => (s.id === spaceId ? { ...s, unread: 0 } : s)),
        );
        return Boolean(data.has_more) && data.messages && data.messages.length > 0;
      }
      return false;
    };

    const tick = async () => {
      if (document.hidden || pollRef.current.busy) return;
      pollRef.current.busy = true;
      try {
        // Catch-up chase: the backend caps a delta at one page, so a tab that
        // slept through a busy morning would otherwise refill 50 messages per
        // 10s tick. Bounded — a server that always says "more" cannot turn
        // this into a hot loop.
        let rounds = 0;
        // eslint-disable-next-line no-await-in-loop
        while (await fetchOnce()) {
          rounds += 1;
          if (rounds >= MAX_CATCHUP_ROUNDS || document.hidden) break;
        }
        setPollStalled(false);
      } catch (error) {
        if (error?.response?.status === 403) {
          if (pollRef.current.spaceId != null) {
            // Removed from the open space mid-session: drop back to the list.
            setErrorBanner('You were removed from that space.');
            closeSpace();
          } else {
            setAccessDenied(true);
          }
        } else {
          setPollStalled(true); // visible "updates paused", never silent
        }
      } finally {
        pollRef.current.busy = false;
      }
    };
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [accessDenied, mergeMessages, closeSpace]);

  // Follow the bottom unless the reader scrolled up; after prepending an
  // older page, hold their place instead of yanking them to the bottom.
  useLayoutEffect(() => {
    const feed = feedRef.current;
    if (!feed) return;
    if (prependRef.current != null) {
      feed.scrollTop = feed.scrollHeight - prependRef.current;
      prependRef.current = null;
      return;
    }
    if (stickToBottomRef.current) feed.scrollTop = feed.scrollHeight;
  }, [messages]);

  const loadOlder = useCallback(async () => {
    const feed = feedRef.current;
    if (!feed || loadingOlder || !hasMore || messages.length === 0) return;
    setLoadingOlder(true);
    prependRef.current = feed.scrollHeight;
    try {
      const page = await teamsService.getMessages(selectedSpace.id, {
        before: messages[0].id,
      });
      stickToBottomRef.current = false;
      setMessages((previous) => [...page.messages, ...previous]);
      setHasMore(Boolean(page.has_more));
    } catch (error) {
      prependRef.current = null;
      surfaceError(error, 'Could not load older messages.');
    } finally {
      setLoadingOlder(false);
    }
  }, [hasMore, loadingOlder, messages, selectedSpace, surfaceError]);

  const onFeedScroll = () => {
    const feed = feedRef.current;
    if (!feed) return;
    stickToBottomRef.current =
      feed.scrollHeight - feed.scrollTop - feed.clientHeight < 80;
    if (feed.scrollTop < PAGE_TRIGGER_PX && hasMore && !loadingOlder) loadOlder();
  };

  // --- composer + mentions ------------------------------------------------

  const onComposerChange = (event) => {
    const value = event.target.value;
    setComposer(value);
    const caret = event.target.selectionStart ?? value.length;
    const match = MENTION_TOKEN.exec(value.slice(0, caret));
    setMentionQuery(match ? match[1] : null);
  };

  const mentionCandidates = (() => {
    if (mentionQuery == null || !selectedSpace) return [];
    const q = mentionQuery.trim().toLowerCase();
    return selectedSpace.members
      .filter((m) => m.name.toLowerCase().includes(q))
      .slice(0, 6);
  })();

  const insertMention = (member) => {
    const input = composerRef.current;
    const caret = input?.selectionStart ?? composer.length;
    const before = composer.slice(0, caret);
    const after = composer.slice(caret);
    setComposer(before.replace(MENTION_TOKEN, `@${member.name} `) + after);
    setMentionQuery(null);
    if (input) setTimeout(() => input.focus(), 0);
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const body = composer.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const message = await teamsService.postMessage(selectedSpace.id, {
        body,
        replyTo: replyTo?.id || null,
        mentionIds: resolveMentionsInText(body, selectedSpace.members),
      });
      stickToBottomRef.current = true;
      appendMessages([message]);
      setComposer('');
      setReplyTo(null);
      setMentionQuery(null);
    } catch (error) {
      surfaceError(error, 'Message not sent — try again.');
    } finally {
      setSending(false);
    }
  };

  const handleComposerKeyDown = (event) => {
    // While the mention menu is open, Enter picks the top match rather than
    // firing off a half-typed name.
    if (mentionCandidates.length > 0 && event.key === 'Enter') {
      event.preventDefault();
      insertMention(mentionCandidates[0]);
      return;
    }
    if (event.key === 'Escape') { setMentionQuery(null); setReplyTo(null); return; }
    if (event.key === 'Enter' && !event.shiftKey) handleSend(event);
  };

  // --- message actions ----------------------------------------------------

  const startReply = (message) => {
    setReplyTo({
      id: message.id,
      author: message.author ? message.author.name : 'Former colleague',
      excerpt: message.body.slice(0, 140),
    });
    composerRef.current?.focus();
  };

  const saveEdit = async () => {
    const body = editing.body.trim();
    if (!body) return;
    try {
      const updated = await teamsService.editMessage(editing.id, {
        body,
        mentionIds: resolveMentionsInText(body, selectedSpace?.members || []),
      });
      mergeMessages([updated]);
      setEditing(null);
    } catch (error) {
      surfaceError(error, 'Could not save the edit.');
    }
  };

  const jumpToMessage = (messageId) => {
    const node = document.getElementById(`teams-msg-${messageId}`);
    if (!node) return;  // parent is older than the loaded window
    node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    node.classList.add('teams-message-flash');
    setTimeout(() => node.classList.remove('teams-message-flash'), 1200);
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await teamsService.deleteMessage(messageId);
      // Soft delete server-side: keep the row, swap it for a tombstone so
      // replies pointing at it still have something to render.
      setMessages((previous) => previous.map(
        (m) => (m.id === messageId ? { ...m, is_deleted: true, body: '' } : m),
      ));
    } catch (error) {
      surfaceError(error, 'Could not delete the message.');
    }
  };

  // --- space actions ------------------------------------------------------

  const refreshSpaces = async () => {
    setSpaces(await teamsService.listSpaces());
  };

  const handleToggleMute = async () => {
    try {
      const result = await teamsService.setMuted(
        selectedSpace.id, !selectedSpace.is_muted,
      );
      setSelectedSpace((previous) => ({ ...previous, is_muted: result.is_muted }));
      await refreshSpaces();
    } catch (error) {
      surfaceError(error, 'Could not change notifications for this space.');
    }
  };

  const handleLeaveSpace = () => {
    setConfirmPrompt({
      message: (
        <>
          <p><strong>Leave “{selectedSpace.name}”?</strong></p>
          <p>
            You will stop seeing its chat and shared todos, and any open item
            assigned to you is handed back to the space.
          </p>
        </>
      ),
      confirmText: 'Leave space',
      onConfirm: async () => {
        try {
          await teamsService.leaveSpace(selectedSpace.id);
          closeSpace();
          await refreshSpaces();
        } catch (error) {
          surfaceError(error, 'Could not leave the space.');
        }
      },
    });
  };

  const handleCreateSpace = async (event) => {
    event.preventDefault();
    if (!newSpaceName.trim()) return;
    try {
      const created = await teamsService.createSpace({
        name: newSpaceName.trim(),
        description: newSpaceDescription.trim(),
        memberIds: newSpaceMemberIds,
      });
      setShowCreateModal(false);
      setNewSpaceName('');
      setNewSpaceDescription('');
      setNewSpaceMemberIds([]);
      await refreshSpaces();
      openSpace(created.id);
    } catch (error) {
      surfaceError(error, 'Could not create the space.');
    }
  };

  const openSettings = () => {
    setSettingsName(selectedSpace.name);
    setSettingsDescription(selectedSpace.description || '');
    setHandoverId('');
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async (event) => {
    event.preventDefault();
    const name = settingsName.trim();
    if (!name) return;
    try {
      const detail = await teamsService.updateSpace(selectedSpace.id, {
        name,
        description: settingsDescription.trim(),
      });
      setSelectedSpace(detail);
      setShowSettingsModal(false);
      await refreshSpaces();
    } catch (error) {
      surfaceError(error, 'Could not save the space details.');
    }
  };

  const handleHandover = () => {
    const target = selectedSpace.members.find(
      (m) => String(m.id) === String(handoverId),
    );
    if (!target) return;
    setConfirmPrompt({
      message: (
        <>
          <p><strong>Hand “{selectedSpace.name}” to {target.name}?</strong></p>
          <p>
            They take over renaming, membership and deletion. You stay a
            member, and you will then be free to leave the space.
          </p>
        </>
      ),
      confirmText: 'Hand over',
      onConfirm: async () => {
        try {
          const detail = await teamsService.transferOwnership(
            selectedSpace.id, target.id,
          );
          setSelectedSpace(detail);
          setShowSettingsModal(false);
          await refreshSpaces();
        } catch (error) {
          surfaceError(error, 'Could not hand the space over.');
        }
      },
    });
  };

  const handleAddMembers = async () => {
    if (addMemberIds.length === 0) return;
    try {
      const detail = await teamsService.manageMembers(selectedSpace.id, {
        add: addMemberIds,
      });
      setSelectedSpace(detail);
      setAddMemberIds([]);
      await refreshSpaces();
    } catch (error) {
      surfaceError(error, 'Could not add members.');
    }
  };

  const handleRemoveMember = async (employeeId) => {
    try {
      const detail = await teamsService.manageMembers(selectedSpace.id, {
        remove: [employeeId],
      });
      setSelectedSpace(detail);
      await refreshSpaces();
      // Their open items came back to the space — show that, don't make the
      // remover discover it on the next poll.
      setTodos(await teamsService.listTodos(selectedSpace.id));
    } catch (error) {
      surfaceError(error, 'Could not remove the member.');
    }
  };

  const handleDeleteSpace = () => {
    setConfirmPrompt({
      message: (
        <>
          <p><strong>Delete “{selectedSpace.name}” for everyone?</strong></p>
          <p>Its chat history and shared todos are removed with it. This cannot be undone.</p>
        </>
      ),
      confirmText: 'Delete space',
      onConfirm: async () => {
        try {
          await teamsService.deleteSpace(selectedSpace.id);
          setShowSettingsModal(false);
          closeSpace();
          await refreshSpaces();
        } catch (error) {
          surfaceError(error, 'Could not delete the space.');
        }
      },
    });
  };

  // --- shared todos -------------------------------------------------------

  const handleAddTodo = async (event) => {
    event.preventDefault();
    if (!todoTitle.trim()) return;
    try {
      const created = await teamsService.createTodo({
        spaceId: selectedSpace.id,
        title: todoTitle.trim(),
        assignee: todoAssignee ? Number(todoAssignee) : null,
        dueDate: todoDueDate || null,
      });
      setTodos((previous) => [created, ...previous]);
      setTodoTitle('');
      setTodoAssignee('');
      setTodoDueDate('');
    } catch (error) {
      surfaceError(error, 'Could not add the todo.');
    }
  };

  const handleToggleTodo = async (todo) => {
    try {
      const updated = await teamsService.updateTodo(todo.id, {
        is_done: !todo.is_done,
      });
      setTodos((previous) =>
        previous.map((t) => (t.id === todo.id ? updated : t)),
      );
    } catch (error) {
      surfaceError(error, 'Could not update the todo.');
    }
  };

  const handleSaveTodoEdit = async () => {
    const title = editingTodo.title.trim();
    if (!title) return;
    try {
      const updated = await teamsService.updateTodo(editingTodo.id, {
        title,
        assignee: editingTodo.assignee ? Number(editingTodo.assignee) : null,
        due_date: editingTodo.due_date || null,
      });
      setTodos((previous) =>
        previous.map((t) => (t.id === updated.id ? updated : t)),
      );
      setEditingTodo(null);
    } catch (error) {
      surfaceError(error, 'Could not save the todo.');
    }
  };

  const handleDeleteTodo = async (todoId) => {
    try {
      await teamsService.deleteTodo(todoId);
      setTodos((previous) => previous.filter((t) => t.id !== todoId));
    } catch (error) {
      surfaceError(
        error,
        'Only the item creator or the space creator can delete a todo.',
      );
    }
  };

  // --- render -------------------------------------------------------------

  if (accessDenied) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="teams-area-container">
          <div className="teams-denied-card">
            <FontAwesomeIcon icon={faLock} className="teams-denied-icon" />
            <h2>Internal staff only</h2>
            <p>
              The Teams area is IMT-internal. Your account does not have access
              to internal chat spaces.
            </p>
          </div>
          <Footer />
        </div>
      </Layout>
    );
  }

  const memberIds = selectedSpace ? selectedSpace.members.map((m) => m.id) : [];
  const openTodos = todos.filter((t) => !t.is_done);
  const doneTodos = todos.filter((t) => t.is_done);
  const totalUnread = spaces.reduce((sum, s) => sum + (s.unread || 0), 0);
  const canManage = Boolean(selectedSpace?.is_creator);
  const assigneeOptions = [
    { value: '', label: 'Unassigned' },
    ...(selectedSpace?.members || []).map((m) => ({
      value: m.id, label: m.name, hint: m.department,
    })),
  ];

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
    <div className="teams-area-container">
      {/* ── HMS screen header ───────────────────────────────────── */}
      <div className="hms-screen-header">
        <div className="header-logo">
          <img src={logo} alt="IMT" className="header-logo-img" />
          <div>
            <h1 className="hms-screen-title">Teams</h1>
            <p className="teams-subtitle">
              Internal chat spaces and shared team todos
            </p>
          </div>
        </div>

        <div className="header-stats-compact">
          <div className="stat-card-compact stat-spaces">
            <div className="stat-value-compact">{spaces.length}</div>
            <div className="stat-label-compact">Spaces</div>
          </div>
          <div className="stat-card-compact stat-unread">
            <div className="stat-value-compact">{totalUnread}</div>
            <div className="stat-label-compact">Unread</div>
          </div>
          {selectedSpace && (
            <div className="stat-card-compact stat-todos">
              <div className="stat-value-compact">{openTodos.length}</div>
              <div className="stat-label-compact">Open</div>
            </div>
          )}
        </div>

        <button
          type="button"
          className="teams-header-cta"
          onClick={() => setShowCreateModal(true)}
        >
          <FontAwesomeIcon icon={faPlus} /> New space
        </button>
      </div>

      {errorBanner && (
        <div className="teams-error-banner" role="alert">
          {errorBanner}
          <button type="button" onClick={() => setErrorBanner('')} aria-label="Dismiss">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      {pollStalled && (
        <div className="teams-stalled-banner">
          Live updates paused — retrying…
        </div>
      )}

      <div className="teams-layout">
        {/* ── Space list ─────────────────────────────────────────── */}
        <aside className="teams-spaces-panel">
          <div className="teams-panel-head">
            <span><FontAwesomeIcon icon={faComments} /> Spaces</span>
          </div>
          {loading && <div className="teams-muted">Loading…</div>}
          {!loading && spaces.length === 0 && (
            <div className="teams-muted">
              No spaces yet — create the first one for your desk.
            </div>
          )}
          <ul className="teams-space-list">
            {spaces.map((space) => (
              <li key={space.id}>
                <button
                  type="button"
                  className={`teams-space-item ${
                    selectedSpace?.id === space.id ? 'active' : ''
                  }`}
                  onClick={() => openSpace(space.id)}
                >
                  <span className="teams-space-row">
                    <span className="teams-space-name">{space.name}</span>
                    {space.is_muted && (
                      <FontAwesomeIcon
                        icon={faBellSlash}
                        className="teams-space-muted-icon"
                        title="Muted"
                      />
                    )}
                    {space.unread > 0 && (
                      <span className={`teams-unread-badge ${space.is_muted ? 'muted' : ''}`}>
                        {space.unread}
                      </span>
                    )}
                  </span>
                  <span className="teams-space-sub">
                    <span className="teams-space-meta">
                      <FontAwesomeIcon icon={faUsers} /> {space.member_count}
                    </span>
                    {space.description && (
                      <span className="teams-space-desc">{space.description}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* ── Feed + todos ───────────────────────────────────────── */}
        {selectedSpace ? (
          <>
            <section className="teams-chat-panel">
              <div className="teams-panel-head">
                <div className="teams-chat-heading">
                  <span className="teams-chat-title">{selectedSpace.name}</span>
                  <span className="teams-chat-members">
                    {selectedSpace.description
                      || selectedSpace.members.map((m) => m.name).join(', ')}
                  </span>
                </div>
                <div className="teams-chat-actions">
                  <button
                    type="button"
                    className={`teams-btn teams-btn-small ${selectedSpace.is_muted ? 'teams-btn-on' : ''}`}
                    onClick={handleToggleMute}
                    title={selectedSpace.is_muted
                      ? 'Unmute — start receiving pings again'
                      : 'Mute — silence pings, keep the unread count'}
                  >
                    <FontAwesomeIcon icon={selectedSpace.is_muted ? faBellSlash : faBell} />
                  </button>
                  <button
                    type="button"
                    className="teams-btn teams-btn-small"
                    onClick={() => setShowMembersModal(true)}
                    title="Who is in this space"
                  >
                    <FontAwesomeIcon icon={faUsers} /> {selectedSpace.members.length}
                  </button>
                  {canManage ? (
                    <button
                      type="button"
                      className="teams-btn teams-btn-small"
                      onClick={openSettings}
                      title="Space settings"
                    >
                      <FontAwesomeIcon icon={faGear} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="teams-btn teams-btn-small teams-btn-danger"
                      onClick={handleLeaveSpace}
                      title="Leave this space"
                    >
                      <FontAwesomeIcon icon={faRightFromBracket} /> Leave
                    </button>
                  )}
                </div>
              </div>
              <div
                className="teams-feed"
                ref={feedRef}
                onScroll={onFeedScroll}
              >
                {hasMore && (
                  <div className="teams-feed-older">
                    <button
                      type="button"
                      className="teams-btn teams-btn-small"
                      onClick={loadOlder}
                      disabled={loadingOlder}
                    >
                      {loadingOlder ? 'Loading…' : 'Load older messages'}
                    </button>
                  </div>
                )}
                {messages.length === 0 && (
                  <div className="teams-muted teams-feed-empty">
                    No messages yet — say hello.
                  </div>
                )}
                {messages.map((message, index) => {
                  const previous = index > 0 ? messages[index - 1] : null;
                  const showDay =
                    !previous ||
                    new Date(previous.created_at).toDateString() !==
                      new Date(message.created_at).toDateString();
                  const showDivider = firstUnreadId && message.id === firstUnreadId;
                  const isEditing = editing?.id === message.id;
                  return (
                    <React.Fragment key={message.id}>
                      {showDay && (
                        <div className="teams-day-separator">
                          <span>{formatDayLabel(message.created_at)}</span>
                        </div>
                      )}
                      {showDivider && (
                        <div className="teams-unread-divider"><span>New messages</span></div>
                      )}
                      <div
                        id={`teams-msg-${message.id}`}
                        className={`teams-message ${message.is_mine ? 'mine' : ''} ${
                          message.mentions_me ? 'mentions-me' : ''}`}
                      >
                        <div className="teams-message-bubble">
                          {!message.is_mine && (
                            <div className="teams-message-author">
                              {message.author ? message.author.name : 'Former colleague'}
                            </div>
                          )}
                          {message.reply_to_preview && (
                            <button
                              type="button"
                              className="teams-quote"
                              onClick={() => jumpToMessage(message.reply_to_preview.id)}
                            >
                              <span className="teams-quote-author">
                                {message.reply_to_preview.author || 'Former colleague'}
                              </span>
                              <span className="teams-quote-text">
                                {message.reply_to_preview.is_deleted
                                  ? 'message deleted'
                                  : message.reply_to_preview.excerpt}
                              </span>
                            </button>
                          )}
                          {message.is_deleted ? (
                            <div className="teams-message-body deleted">message deleted</div>
                          ) : isEditing ? (
                            <div className="teams-edit-box">
                              <textarea
                                value={editing.body}
                                maxLength={4000}
                                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    saveEdit();
                                  }
                                  if (e.key === 'Escape') setEditing(null);
                                }}
                              />
                              <div className="teams-edit-actions">
                                <button
                                  type="button"
                                  className="teams-btn teams-btn-small"
                                  onClick={() => setEditing(null)}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="teams-btn teams-btn-primary teams-btn-small"
                                  onClick={saveEdit}
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="teams-message-body">
                              <MessageBody body={message.body} mentions={message.mentions} />
                            </div>
                          )}
                          <div className="teams-message-time">
                            {formatTime(message.created_at)}
                            {message.edited_at && !message.is_deleted && (
                              <span className="teams-edited">edited</span>
                            )}
                            {!message.is_deleted && !isEditing && (
                              <span className="teams-message-tools">
                                <button type="button" title="Reply" onClick={() => startReply(message)}>
                                  <FontAwesomeIcon icon={faReply} />
                                </button>
                                {message.is_mine && (
                                  <>
                                    <button type="button" title="Edit" onClick={() => setEditing({ id: message.id, body: message.body })}>
                                      <FontAwesomeIcon icon={faPen} />
                                    </button>
                                    <button type="button" title="Delete" onClick={() => handleDeleteMessage(message.id)}>
                                      <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                  </>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
              {replyTo && (
                <div className="teams-reply-strip">
                  <div className="teams-reply-strip-text">
                    <span className="teams-quote-author">Replying to {replyTo.author}</span>
                    <span className="teams-quote-text">{replyTo.excerpt}</span>
                  </div>
                  <button type="button" onClick={() => setReplyTo(null)} title="Cancel reply">
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}

              <form className="teams-composer" onSubmit={handleSend}>
                {mentionCandidates.length > 0 && (
                  <div className="teams-mention-menu">
                    {mentionCandidates.map((member, i) => (
                      <button
                        type="button"
                        key={member.id}
                        className={`teams-mention-option ${i === 0 ? 'first' : ''}`}
                        onClick={() => insertMention(member)}
                      >
                        <span className="teams-picker-name">{member.name}</span>
                        {member.department && (
                          <span className="teams-picker-dept">{member.department}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                <textarea
                  ref={composerRef}
                  value={composer}
                  onChange={onComposerChange}
                  onKeyDown={handleComposerKeyDown}
                  placeholder={`Message ${selectedSpace.name}…  (@ to mention)`}
                  rows={2}
                  maxLength={4000}
                />
                <button
                  type="submit"
                  className="teams-btn teams-btn-primary teams-send-btn"
                  disabled={!composer.trim() || sending}
                  aria-label="Send message"
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                </button>
              </form>
            </section>

            <aside className="teams-todo-panel">
              <div className="teams-panel-head">
                <span>
                  <FontAwesomeIcon icon={faListCheck} /> Shared todos
                </span>
                <span className="teams-todo-count">
                  {openTodos.length} open
                </span>
              </div>
              <form className="teams-todo-form" onSubmit={handleAddTodo}>
                <input
                  type="text"
                  className="teams-todo-title-input"
                  value={todoTitle}
                  maxLength={200}
                  placeholder="Add a shared todo…"
                  onChange={(e) => setTodoTitle(e.target.value)}
                />
                <TeamsSelect
                  value={todoAssignee}
                  onChange={setTodoAssignee}
                  options={assigneeOptions}
                  placeholder="Unassigned"
                  ariaLabel="Assign this todo to"
                />
                <div className="teams-todo-form-row">
                  <TodoDatePicker
                    name="teams-todo-due"
                    value={todoDueDate}
                    placeholder="Due date"
                    className="teams-date"
                    onChange={(e) => setTodoDueDate(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="teams-btn teams-btn-primary teams-btn-small teams-todo-add"
                    disabled={!todoTitle.trim()}
                    aria-label="Add shared todo"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                </div>
              </form>
              <div className="teams-todo-list">
                {todos.length === 0 && (
                  <div className="teams-muted">Nothing on the list yet.</div>
                )}
                {[...openTodos, ...doneTodos].map((todo) => (
                  <div
                    key={todo.id}
                    className={`teams-todo-item ${todo.is_done ? 'done' : ''} ${
                      isOverdue(todo) ? 'overdue' : ''
                    }`}
                  >
                    {(
                      <>
                        <label className="teams-todo-main">
                          <input
                            type="checkbox"
                            checked={todo.is_done}
                            onChange={() => handleToggleTodo(todo)}
                          />
                          <span className="teams-todo-title">{todo.title}</span>
                        </label>
                        <div className="teams-todo-meta">
                          {todo.assignee_detail && (
                            <span className="teams-todo-chip">
                              {todo.assignee_detail.name}
                            </span>
                          )}
                          {todo.due_date && (
                            <span className={`teams-todo-chip due ${isOverdue(todo) ? 'late' : ''}`}>
                              {formatDueDate(todo.due_date)}
                            </span>
                          )}
                          {todo.is_done && todo.completed_by && (
                            <span className="teams-todo-chip done-by">
                              ✓ {todo.completed_by.name}
                            </span>
                          )}
                          <button
                            type="button"
                            className="teams-todo-edit-btn"
                            title="Edit todo"
                            onClick={() => setEditingTodo({
                              id: todo.id,
                              title: todo.title,
                              assignee: todo.assignee ?? '',
                              due_date: todo.due_date || '',
                            })}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </button>
                          <button
                            type="button"
                            className="teams-todo-delete"
                            title="Delete todo"
                            onClick={() => handleDeleteTodo(todo.id)}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </aside>
          </>
        ) : (
          <section className="teams-empty-state">
            <FontAwesomeIcon icon={faComments} className="teams-empty-icon" />
            <h2>Pick a space</h2>
            <p>
              Choose a space on the left, or create one and invite your
              colleagues — every member sees the chat and the shared todo list.
            </p>
            <button
              type="button"
              className="teams-btn teams-btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} /> New space
            </button>
          </section>
        )}
      </div>

      {/* ── Create-space modal ──────────────────────────────────── */}
      {showCreateModal && (
        <TeamsModal title="New team space" onClose={() => setShowCreateModal(false)}>
          <form onSubmit={handleCreateSpace}>
            <div className="teams-modal-label">Name</div>
            <input
              type="text"
              className="teams-modal-input"
              placeholder="Space name (e.g. Bunkers Desk)"
              value={newSpaceName}
              maxLength={120}
              autoFocus
              onChange={(e) => setNewSpaceName(e.target.value)}
            />
            <div className="teams-modal-label">Description</div>
            <input
              type="text"
              className="teams-modal-input"
              placeholder="What this space is for (optional)"
              value={newSpaceDescription}
              maxLength={300}
              onChange={(e) => setNewSpaceDescription(e.target.value)}
            />
            <div className="teams-modal-label">Invite members</div>
            <ColleaguePicker
              colleagues={colleagues}
              excludeIds={[]}
              selectedIds={newSpaceMemberIds}
              onToggle={(id) =>
                setNewSpaceMemberIds((previous) =>
                  previous.includes(id)
                    ? previous.filter((x) => x !== id)
                    : [...previous, id],
                )
              }
            />
            <div className="teams-modal-actions">
              <button
                type="button"
                className="teams-btn"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="teams-btn teams-btn-primary"
                disabled={!newSpaceName.trim()}
              >
                Create space
              </button>
            </div>
          </form>
        </TeamsModal>
      )}

      {/* ── Members modal — readable by every member, managed by the
             creator (and superusers, who the backend also lets through) ── */}
      {showMembersModal && selectedSpace && (
        <TeamsModal
          title={`Members — ${selectedSpace.name}`}
          onClose={() => setShowMembersModal(false)}
        >
          <div className="teams-member-list">
            {selectedSpace.members.map((member) => (
              <div key={member.id} className="teams-member-row">
                <span className="teams-picker-name">{member.name}</span>
                {member.department && (
                  <span className="teams-picker-dept">{member.department}</span>
                )}
                {member.is_creator ? (
                  <span className="teams-todo-chip owner">owner</span>
                ) : canManage ? (
                  <button
                    type="button"
                    className="teams-todo-delete"
                    title="Remove from space"
                    onClick={() => handleRemoveMember(member.id)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          {canManage ? (
            <>
              <div className="teams-modal-label">Add colleagues</div>
              <ColleaguePicker
                colleagues={colleagues}
                excludeIds={memberIds}
                selectedIds={addMemberIds}
                onToggle={(id) =>
                  setAddMemberIds((previous) =>
                    previous.includes(id)
                      ? previous.filter((x) => x !== id)
                      : [...previous, id],
                  )
                }
              />
              <div className="teams-modal-actions">
                <button
                  type="button"
                  className="teams-btn teams-btn-primary"
                  disabled={addMemberIds.length === 0}
                  onClick={handleAddMembers}
                >
                  Add selected
                </button>
              </div>
            </>
          ) : (
            <p className="teams-modal-note">
              Only {selectedSpace.created_by?.name || 'the space owner'} can add
              or remove members here.
            </p>
          )}
        </TeamsModal>
      )}

      {/* ── Edit a shared todo ──────────────────────────────────────
             In a modal rather than inline: the row lives inside a scrolling
             list, and the house calendar popup would be clipped by it. ── */}
      {editingTodo && (
        <TeamsModal title="Edit shared todo" onClose={() => setEditingTodo(null)}>
          <div className="teams-modal-label">Title</div>
          <input
            type="text"
            className="teams-modal-input"
            value={editingTodo.title}
            maxLength={200}
            autoFocus
            onChange={(e) => setEditingTodo({ ...editingTodo, title: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleSaveTodoEdit(); }
            }}
          />
          <div className="teams-modal-label">Assigned to</div>
          <TeamsSelect
            value={editingTodo.assignee ?? ''}
            onChange={(value) => setEditingTodo({ ...editingTodo, assignee: value })}
            options={assigneeOptions}
            placeholder="Unassigned"
            ariaLabel="Reassign this todo"
          />
          <div className="teams-modal-label">Due date</div>
          <TodoDatePicker
            name={`teams-todo-due-${editingTodo.id}`}
            value={editingTodo.due_date || ''}
            placeholder="No due date"
            className="teams-date teams-date-modal"
            onChange={(e) => setEditingTodo({ ...editingTodo, due_date: e.target.value })}
          />
          <div className="teams-modal-actions">
            <button
              type="button"
              className="teams-btn"
              onClick={() => setEditingTodo(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="teams-btn teams-btn-primary"
              onClick={handleSaveTodoEdit}
              disabled={!editingTodo.title.trim()}
            >
              Save
            </button>
          </div>
        </TeamsModal>
      )}

      {/* ── Space settings (owner) ──────────────────────────────── */}
      {showSettingsModal && selectedSpace && (
        <TeamsModal
          title={`Space settings — ${selectedSpace.name}`}
          onClose={() => setShowSettingsModal(false)}
        >
          <form onSubmit={handleSaveSettings}>
            <div className="teams-modal-label">Name</div>
            <input
              type="text"
              className="teams-modal-input"
              value={settingsName}
              maxLength={120}
              onChange={(e) => setSettingsName(e.target.value)}
            />
            <div className="teams-modal-label">Description</div>
            <input
              type="text"
              className="teams-modal-input"
              placeholder="What this space is for (optional)"
              value={settingsDescription}
              maxLength={300}
              onChange={(e) => setSettingsDescription(e.target.value)}
            />
            <div className="teams-modal-actions">
              <button
                type="submit"
                className="teams-btn teams-btn-primary"
                disabled={!settingsName.trim()}
              >
                Save details
              </button>
            </div>
          </form>

          <div className="teams-modal-divider" />
          <div className="teams-modal-label">
            <FontAwesomeIcon icon={faUserShield} /> Hand the space over
          </div>
          <p className="teams-modal-note">
            The owner cannot leave a space — hand it to another member first,
            and they take over members, renaming and deletion.
          </p>
          <div className="teams-handover-row">
            <TeamsSelect
              value={handoverId}
              onChange={setHandoverId}
              options={selectedSpace.members
                .filter((m) => !m.is_creator)
                .map((m) => ({ value: m.id, label: m.name, hint: m.department }))}
              placeholder="Choose a member…"
              ariaLabel="New owner"
            />
            <button
              type="button"
              className="teams-btn"
              disabled={!handoverId}
              onClick={handleHandover}
            >
              Hand over
            </button>
          </div>

          <div className="teams-modal-divider" />
          <div className="teams-modal-actions">
            <button
              type="button"
              className="teams-btn teams-btn-danger"
              onClick={handleDeleteSpace}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete this space
            </button>
          </div>
        </TeamsModal>
      )}

      {confirmPrompt && (
        <ConfirmationPopup
          message={confirmPrompt.message}
          confirmButtonText={confirmPrompt.confirmText}
          cancelButtonText="Cancel"
          onCancel={() => setConfirmPrompt(null)}
          onConfirm={async () => {
            const run = confirmPrompt.onConfirm;
            setConfirmPrompt(null);
            await run();
          }}
        />
      )}

      <Footer />
    </div>
    </Layout>
  );
}

export default TeamsArea;
